// syncQueue.js — Phase 35e (sync retry queue)
//
// Durable push-retry queue stored in IndexedDB. When dataService.pushInBackground
// fails with a transient error (network, 5xx, 408, 429), the failed request is
// enqueued here. `flushSyncQueue()` walks pending entries and retries them
// via the api client.
//
// Public surface:
//   enqueue({table, targetLocalUuid, method, path, body, error}) → entry
//   listPending() → entry[]                                sorted by created_at asc
//   markDone(localUuid)                                      // success
//   markFailed(localUuid, error)                             // gave up (4xx)
//   recordTransientFailure(localUuid, error)                 // 5xx/408/429/network
//   isPermanentFailure(err) → boolean                        // pure
//   flushSyncQueue() → { flushed, succeeded, failed, transient }
//
// Pure helpers (no Dexie needed): isPermanentFailure, classifyHttpStatus.
//
// Design notes:
//   - localUuid is the queue entry's UUID; distinct from the target row's
//     local_uuid. (A row could re-enter the queue multiple times during
//     edit-after-failure flows.)
//   - body is JSON.stringified once at enqueue time so flush doesn't
//     depend on caller state.
//   - MAX_ATTEMPTS caps retries to prevent infinite growth. After
//     MAX_ATTEMPTS transient failures, entry is marked 'failed'.
//   - Statuses: 'pending' | 'failed' (permanent) — terminal.
//   - 5xx/408/429/network errors are transient. All other 4xx (401, 403,
//     404, 410, etc.) are permanent and won't retry — they indicate a
//     logic problem, not a network blip.

import { uuid, nowIso } from './ids.js';
import { db } from './localDb.js';

export const MAX_ATTEMPTS = 5;

const TRANSIENT_4XX = new Set([408, 429]); // request timeout, too many requests

// Pure: classify an HTTP status code (or absence of one) as transient.
export function isPermanentFailure(err) {
  // No status → network error (fetch never resolved). Treat as
  // transient so we'll retry. The user is offline-ish; better to
  // queue and try again than to mark the row's data lost.
  if (!err || err.status == null) return false;
  if (err.status >= 500) return false;          // 5xx — server-side, retry
  if (TRANSIENT_4XX.has(err.status)) return false; // 408, 429 — retry
  return true;                                   // everything else (incl 401/403/404/410)
}

export async function enqueue({ table, targetLocalUuid, method, path, body, error }) {
  const entry = {
    local_uuid: uuid(),
    table,
    target_local_uuid: targetLocalUuid || null,
    method,
    path,
    body: body !== undefined ? JSON.stringify(body) : null,
    attempts: 1,
    status: 'pending',
    last_error: error ? (error.message || String(error)) : null,
    last_error_status: error && error.status != null ? error.status : null,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  await db.push_queue.add(entry);
  return entry;
}

export async function listPending() {
  return db.push_queue.where('status').equals('pending').sortBy('created_at');
}

export async function listAll() {
  return db.push_queue.toArray();
}

export async function markDone(localUuid) {
  await db.push_queue.delete(localUuid);
  return null;
}

export async function markFailed(localUuid, error) {
  await db.push_queue.update(localUuid, {
    status: 'failed',
    last_error: error ? (error.message || String(error)) : null,
    last_error_status: error && error.status != null ? error.status : null,
    updated_at: nowIso(),
  });
  return db.push_queue.get(localUuid);
}

export async function recordTransientFailure(localUuid, error) {
  const row = await db.push_queue.get(localUuid);
  if (!row) return null;
  const attempts = (row.attempts || 1) + 1;
  const update = {
    attempts,
    last_error: error ? (error.message || String(error)) : null,
    last_error_status: error && error.status != null ? error.status : null,
    updated_at: nowIso(),
  };
  if (attempts >= MAX_ATTEMPTS) {
    update.status = 'failed';
  }
  await db.push_queue.update(localUuid, update);
  return db.push_queue.get(localUuid);
}

// flushSyncQueue() — replay all pending entries through the api client.
// Returns counters. Caller is responsible for ensuring the connection is
// available (i.e. isConnected() === true) — we don't check here so this
// works in tests with mocked fetch.
//
// Pass an `apiClient` injectable for testing; in production we import
// the real api from ../api/client.js (lazily, to avoid circular import).
export async function flushSyncQueue(opts = {}) {
  const apiClient = opts.api || (await import('../api/client.js')).api;
  const pending = await listPending();
  let succeeded = 0, failed = 0, transient = 0;
  for (const entry of pending) {
    const body = entry.body != null ? JSON.parse(entry.body) : undefined;
    try {
      await apiClient[entry.method](entry.path, body);
      await markDone(entry.local_uuid);
      succeeded++;
    } catch (e) {
      if (isPermanentFailure(e)) {
        await markFailed(entry.local_uuid, e);
        failed++;
      } else {
        await recordTransientFailure(entry.local_uuid, e);
        transient++;
        // If we just crossed MAX_ATTEMPTS, recordTransientFailure flipped
        // status to 'failed'; count it in `failed` for the caller's tally.
        const after = await db.push_queue.get(entry.local_uuid);
        if (after && after.status === 'failed') failed++;
      }
    }
  }
  return { flushed: pending.length, succeeded, failed, transient };
}

export const __test__ = {
  MAX_ATTEMPTS,
  TRANSIENT_4XX: Array.from(TRANSIENT_4XX),
  isPermanentFailure,
};
